import { Paginator, Spinner, Stack } from "@adfenix/alexandria";
import { PropertyService } from "@adfenix/types";
import { Modal } from "components/Modal";
import { ReadLayerContext } from "components/ReadLayer/ReadLayerQueryContext";
import { ReadLayerWrapper } from "components/ReadLayer/ReadLayerWrapper";
import { SearchField } from "components/ReadLayer/SearchField";
import { HqIdRouteParams } from "components/SelectHqOfficeCombobox/helper";
import { useOfficeAndHq } from "hooks/useOfficeAndHq";
import useUserInfo from "hooks/useUserInfo";
import { useContext } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Props {
  closeModal: () => void;
}

const Listings = ({}) => {
  const { t } = useTranslation();

  const ctx = useContext(ReadLayerContext);
  const { accessLevel } = useUserInfo();

  const properties = ctx?.data
    ?.documents as PropertyService.PropertyWithCampaignsSearchModel[];
  const { hq } = useOfficeAndHq();
  const { programId } = useParams<{ programId: string }>();
  if (!properties || ctx?.isLoading) return <Spinner />;

  return (
    <table className="table" style={{ width: "100%" }}>
      {properties.map(x => (
        <tr key={x.id}>
          <td>{x.location.address}</td>
          <td>
            <a
              href={`/customer/hq/${hq?.id}/order/setup?programId=${programId}&propertyId=${x.id}&test=true&officeId=${x.office.id}`}
              target="_blank"
              rel="noreferrer"
            >
              {accessLevel === "Multi-Brand"
                ? t(
                    "programBuilder.program.details.testOrderPage.link.customer"
                  )
                : t(
                    "programBuilder.program.details.testOrderPage.link.orderPage"
                  )}
            </a>
          </td>
          {accessLevel === "Multi-Brand" && (
            <td>
              <a
                href={`/hq/${hq?.id}/ap-order/?programId=${programId}&propertyId=${x.id}&test=true&officeId=${x.office.id}`}
                target="_blank"
                rel="noreferrer"
              >
                {t("programBuilder.program.details.testOrderPage.link.COPS")}
              </a>
            </td>
          )}
          {accessLevel === "Multi-Brand" && (
            <td>
              <a
                href={`/customer/hq/${hq?.id}/order/preview/?programId=${programId}&propertyId=${x.id}&test=true&officeId=${x.office.id}&map=true`}
                target="_blank"
                rel="noreferrer"
              >
                {t("programBuilder.program.details.testOrderPage.link.MAP")}
              </a>
            </td>
          )}
        </tr>
      ))}
    </table>
  );
};

const Pagination = () => {
  const ctx = useContext(ReadLayerContext);

  if (!ctx) return null;

  return (
    <Paginator
      onChange={page => ctx.updateQuery({ page })}
      numbersInMiddle={5}
      pageSize={ctx.query.pageSize ?? 1}
      numberOfItems={Math.min(ctx.data?.totalNumberOfDocuments ?? 10, 10000)}
      activePage={ctx.query.page ?? 1}
    />
  );
};

export const PropertyTestOrderPageModal = ({ closeModal }: Props) => {
  const { t } = useTranslation();

  const { hqId } = useParams<HqIdRouteParams>();

  const filters = [
    {
      column: "hq.id",
      value: hqId
    }
  ];

  return (
    <Modal show={true} onClose={closeModal}>
      <Modal.Card>
        <Modal.Card.Head onClose={closeModal}>
          <Modal.Card.Title>
            {t("programBuilder.program.details.testOrderPage.title")}
          </Modal.Card.Title>
        </Modal.Card.Head>

        <Modal.Card.Body>
          <ReadLayerWrapper
            serviceName="property"
            indexName="property"
            sortOnColumn="created"
            sortAscending={false}
            filters={filters}
          >
            <Stack spacing="l">
              <SearchField
                minWidth={400}
                placeholder={t(
                  "programBuilder.program.details.testOrderPage.search.placeholder"
                )}
                columns={[
                  "location.address",
                  "id",
                  "agent.email",
                  "url",
                  "nonCanonicalUrls"
                ]}
              />

              <Listings />

              <Pagination />
            </Stack>
          </ReadLayerWrapper>
        </Modal.Card.Body>

        <Modal.Card.Foot></Modal.Card.Foot>
      </Modal.Card>
    </Modal>
  );
};
